// A10: a card reads the same MessageView as the item page; reading records no decision.
// BUGS.md T73 (U426): on a card each message is folded to its head until pressed; a reply box opens the newest above it.
import { ActionButton } from '../parts/index.js';
import { useEffect, useState, type ReactNode } from 'react';
import { dayMonth } from '../day.js';
import { threadRead, type ThreadView } from './api.js';
import { MessageView } from './MessageView.js';

export function useCardThread(store: string, signalId: string | null): ThreadView | null {
  const [loaded, setLoaded] = useState<{ store: string; signalId: string; thread: ThreadView } | null>(null);
  useEffect(() => {
    let live = true;
    if (signalId) void threadRead(store, signalId).then(({ thread }) => { if (live) setLoaded({ store, signalId, thread }); }).catch(() => { if (live) setLoaded(null); });
    return () => { live = false; };
  }, [store, signalId]);
  return loaded?.store === store && loaded.signalId === signalId ? loaded.thread : null;
}

export function CardMessage({ store, signalId, channel, thread, title, open: openNewest = false, children }: { store: string; signalId: string; channel: string; thread: ThreadView | null; title?: string; open?: boolean; children?: ReactNode }) {
  const [whole, setWhole] = useState(false);
  const [older, setOlder] = useState<ThreadView['messages']>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setWhole(false); setOlder([]); }, [store, signalId, thread]);
  const newest = thread?.messages.find((m) => m.id === thread.newestId) ?? thread?.messages.at(-1);
  if (!newest || !thread) return null;
  const origin = thread.messages.find((m) => m.id === signalId);
  const count = thread.messages.length + (thread.earlier ?? 0);
  const open = async () => {
    if (whole) { setWhole(false); return; }
    setBusy(true);
    try {
      let page = thread;
      const all = new Map(thread.messages.map((m) => [m.id, m]));
      while ((page.earlier ?? 0) > 0) {
        const cursor = page.messages.filter((m) => m.id !== signalId)[0] ?? page.messages[0];
        if (!cursor) break;
        const next = (await threadRead(store, signalId, cursor.id)).thread;
        const size = all.size;
        for (const m of next.messages) all.set(m.id, m);
        if (all.size === size) break;
        page = next;
      }
      setOlder([...all.values()].sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt) || a.id.localeCompare(b.id)));
      setWhole(true);
    } finally { setBusy(false); }
  };
  return <div className="card-message">
    {origin && origin.id !== newest.id ? <p className="note">Cobrain raised this from the mail of {dayMonth(new Date(origin.occurredAt))}; this is the newest</p> : null}
    <MessageView key={newest.id} message={newest} channel={channel} store={store} fold open={openNewest} {...(title ? { title } : {})} />
    <ActionButton reading type="button" className="hx-link m" disabled={busy} aria-expanded={whole} onClick={() => void open().catch(() => {})}>{channel === 'teams' ? `Earlier in the chat (${count})` : `The whole thread (${count})`}</ActionButton>
    {whole ? older.map((m) => <MessageView key={m.id} message={m} channel={channel} store={store} fold {...(title ? { title } : {})} />) : null}
    {children}
  </div>;
}
